import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

interface NavItem {
  id: string;
  label: string;
  url?: string | null;
  is_active?: boolean | null;
  parent_id?: string | null;
}

interface ServicesDropdownProps {
  navItems: NavItem[];
  onNavigate?: () => void;
}

export function ServicesDropdown({ navItems, onNavigate }: ServicesDropdownProps) {
  const categories = useMemo(
    () => ([
      { id: "inbound", label: "Inbound Voice Agents" },
      { id: "outbound", label: "Outbound Voice Agents" },
      { id: "ai", label: "AI Copilots" },
      { id: "automation", label: "Automation" },
    ]),
    []
  );

  const servicesByCategory: Record<string, { id: string; label: string; url: string }[]> = {
    inbound: [
      { id: "realtor-office", label: "Realtor Office", url: "/services/inbound-voice-agents/realtor-office" },
      { id: "insurance-companies", label: "Insurance Companies", url: "/services/inbound-voice-agents/insurance-companies" },
      { id: "healthcare-providers", label: "Healthcare Providers", url: "/services/inbound-voice-agents/healthcare-providers" },
      { id: "restaurant-chains", label: "Restaurant Chains", url: "/services/inbound-voice-agents/restaurant-chains" },
      { id: "customer-service", label: "Customer Service", url: "/services/inbound-voice-agents/customer-service" },
      { id: "saas-companies", label: "SaaS Companies", url: "/services/inbound-voice-agents/saas-companies" },
    ],
    outbound: [
      { id: "sales-outreach", label: "Sales Outreach", url: "/services/outbound-voice-agents/sales-outreach" },
      { id: "customer-retention", label: "Customer Retention", url: "/services/outbound-voice-agents/customer-retention" },
      { id: "appointment-reminders", label: "Appointment Reminders", url: "/services/outbound-voice-agents/appointment-reminders" },
      { id: "survey-collection", label: "Survey Collection", url: "/services/outbound-voice-agents/market-research" },
      { id: "debt-collection", label: "Debt Collection", url: "/services/outbound-voice-agents/debt-collection" },
      { id: "marketing-agency", label: "Marketing Agency", url: "/services/outbound-voice-agents/marketing-agency" },
    ],
    ai: [
      { id: "agent-assistance", label: "Agent Assistance", url: "/services/ai-copilots/agent-assistance" },
      { id: "document-processing", label: "Document Processing", url: "/services/ai-copilots/document-processing" },
      { id: "meeting-transcription", label: "Meeting Transcription", url: "/services/ai-copilots/meeting-transcription" },
      { id: "email-drafting", label: "Email Drafting", url: "/services/ai-copilots/email-drafting" },
      { id: "scheduling-optimization", label: "Scheduling Optimizations", url: "/services/ai-copilots/scheduling-optimization" },
      { id: "research-assistance", label: "Research Assistance", url: "/services/ai-copilots/research-assistance" },
    ],
    automation: [
      { id: "document-workflow", label: "Document Workflow", url: "/services/automation/document-workflow" },
      { id: "employee-onboarding", label: "Employee Onboarding", url: "/services/automation/employee-onboarding" },
      { id: "inventory-management", label: "Inventory Management", url: "/services/automation/inventory-management" },
      { id: "customer-communications", label: "Customer Communications", url: "/services/automation/customer-communications" },
      { id: "process-orchestrations", label: "Process Orchestrations", url: "/services/automation/process-orchestration" },
      { id: "data-integrations", label: "Data Integrations", url: "/services/automation/data-integration" },
    ],
  };

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const activeCategory = categories.find((c) => c.id === activeCategoryId) || null;
  const services = activeCategoryId ? servicesByCategory[activeCategoryId] || [] : [];

  const handleLinkClick = () => {
    onNavigate?.();
  };

  const renderServiceLink = (item: NavItem) => {
    const href = item.url || "#";
    const isExternal = /^https?:\/\//i.test(href);

    if (isExternal) {
      return (
        <a
          key={item.id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleLinkClick}
          className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        key={item.id}
        to={href}
        onClick={handleLinkClick}
        className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
      >
        {item.label}
      </Link>
    );
  };

  return (
    <div className="w-full max-w-5xl">
      {/* Categories grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map((cat) => {
          const isActive = activeCategoryId === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategoryId(isActive ? null : cat.id)}
              className={`text-left w-full rounded-lg border bg-white px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 hover:bg-gray-50 ${
                isActive ? "border-gray-300" : "border-gray-200"
              }`}
              aria-expanded={isActive}
            >
              <span className="block text-base font-medium text-gray-900">{cat.label}</span>
              <span className="block text-sm text-gray-600">{isActive ? "Hide services" : "View services"}</span>
            </button>
          );
        })}
      </div>

      {/* Services panel for the active category */}
      {activeCategory && services.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            {activeCategory.label}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
            {services.map((srv) => renderServiceLink(srv))}
          </div>
        </div>
      )}

      {/* Empty state when no category selected */}
      {!activeCategory && (
        <p className="mt-3 text-sm text-gray-600">Select a category to view its services.</p>
      )}
    </div>
  );
}
